/**
 * THE SECOND PRICE TRUTH ON THE DEALER RAIL — a decision, never a fallback.
 *
 * The daily dealer snapshot already owns SPOT. This object may add a LIVE marker only when an
 * SPX price brings its own usable clock and clears the price budget. ⛔ ES is not an approximation
 * here: absent SPX beats a confident line 16–23 points away on another instrument.
 */
export type LiveMarker =
  | { kind: "draw"; price: number; ageS: number; source: string }
  | { kind: "absent"; reason: string };

/** Keep the two quantities readable without rounding a measured breach back onto its budget. */
function ageText(seconds: number): string {
  if (seconds >= 3600) return `${(seconds / 3600).toFixed(1)}h`;
  return `${Number.isInteger(seconds) ? seconds : Number(seconds.toFixed(3))}s`;
}

export function liveMarker(input: {
  price: number | null | undefined;
  asOfMs: number | null | undefined;
  nowMs: number;
  budgetS: number;
  source: string;
  transport?: string | null;
}): LiveMarker {
  // ⛔ THE ROUTE'S REFUSAL WINS. Paraphrasing it here would create a second diagnosis and discard
  // the producer's exact evidence — especially today's allowlist answer naming SPX as unsupported.
  if (input.transport !== undefined && input.transport !== null) {
    return { kind: "absent", reason: input.transport };
  }

  // ⛔ ZERO IS A PRICE, NOT AN EMPTY VALUE. Drawing it would be absurd; treating it as absence
  // without saying why would be silent degradation. Non-finite values belong to the same refusal.
  if (input.price === undefined || input.price === null || !Number.isFinite(input.price) || input.price <= 0) {
    return { kind: "absent", reason: "no usable live price" };
  }

  // A number without knowledge time cannot be called live. Defaulting this to `nowMs` would turn
  // the one state we cannot age into the freshest possible state — the exact cast this type blocks.
  if (input.asOfMs === undefined || input.asOfMs === null || !Number.isFinite(input.asOfMs)) {
    return { kind: "absent", reason: "a price with no time is not a live price" };
  }

  if (!Number.isFinite(input.budgetS) || input.budgetS < 0) {
    return { kind: "absent", reason: "live price freshness budget was not served" };
  }

  const ageS = (input.nowMs - input.asOfMs) / 1000;
  // ⛔ A FUTURE STAMP IS A CLOCK FAULT, NOT AGE ZERO. Clamping would hide disagreement between the
  // producer and this browser under the visual language for a perfectly fresh print.
  if (ageS < 0) {
    return { kind: "absent", reason: `clock disagreement, ${ageText(-ageS)} in the future` };
  }

  if (ageS > input.budgetS) {
    return {
      kind: "absent",
      reason: `SPX live price ${ageText(ageS)} old, budget ${ageText(input.budgetS)}`,
    };
  }

  return { kind: "draw", price: input.price, ageS, source: input.source };
}
